/**
 * Record the consultant's reply on submissions logged earlier: code A / B / C / D, the day it came
 * back and the comments. One submission (a row) or a whole transmittal (a group, or the selection).
 * The drawing status follows the reply exactly like logging a submission does — only for a drawing's
 * latest submission; the footer says what will happen before saving.
 * Wave 3 (15 #9): "From email" — paste the consultant's email and the AI assistant fills the code, date and
 * comments (PasteEmail.js); the person checks them and saves.
 */
import { useMemo, useState } from 'react';
import { Loader2, Reply, Sparkles, TriangleAlert } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { formatDate, formatNumber } from '@/lib/format';
import { getErrorMessage } from '@/lib/errors';
import { toneClass } from '@/lib/status';
import { cn } from '@/lib/utils';
import {
  REPLY_CODES,
  REPLY_NEXT_STATUS,
  STATUS_LABELS,
  drawingTitle,
  labelize,
  realDrawingId,
  todayIso,
} from './model';
import { PasteEmail } from './PasteEmail';

const fmt = (value) => formatNumber(value, { maxDecimals: 0 });
const SHORT_CODE_LABELS = { A: 'Approved', B: 'Approved w/ comments', C: 'Revise & resubmit', D: 'Rejected' };

/** What the AI read, in one line + what the person should check. */
function aiSummary(result, rows) {
  const item = REPLY_CODES.find((entry) => entry.code === result.code);
  const parts = [item ? `code ${item.code} · ${item.label}` : 'no code found', result.reply_date ? formatDate(result.reply_date) : 'no reply date', `${result.confidence} confidence`];
  const warnings = [];
  if (result.mixed_codes) warnings.push('The email gives different codes per drawing — record them one by one.');
  if (rows.length > 1 && result.matches.length < rows.length) warnings.push(`The email names ${result.matches.length} of these ${rows.length} submissions.`);
  if (rows.length === 1 && !result.matches.length) warnings.push('The email does not name this drawing — check it is the right one.');
  if (result.notes) warnings.push(result.notes);
  return { line: parts.join(' · '), warnings };
}

/** The A / B / C / D letter as a 22px square badge in the reply's tone. */
export function ReplyCodeBadge({ code, tone, className }) {
  return (
    <span
      className={cn(toneClass(tone), 'inline-grid h-[22px] w-[22px] shrink-0 place-items-center rounded-md bg-[color:var(--tone-bg)] text-[11.5px] font-extrabold text-[color:var(--tone-ink)]', className)}
      aria-hidden="true"
    >
      {code}
    </span>
  );
}

/** What saving `reply` does to the drawings of `rows` — 'Drawing becomes Completed w/ remarks'. */
export function replyEffect(rows, reply) {
  if (!reply) return 'Pick the code written on the returned transmittal.';
  const next = REPLY_NEXT_STATUS[reply];
  const latest = rows.filter((row) => row.isLatest);
  const label = labelize(next, STATUS_LABELS);
  if (rows.length === 1) {
    return latest.length
      ? `Drawing becomes ${label}`
      : `Drawing stays ${labelize(rows[0].drawing.status, STATUS_LABELS)} (a newer submission was logged)`;
  }
  if (!latest.length) return 'The drawings keep their status (newer submissions were logged)';
  return latest.length === rows.length
    ? `${fmt(rows.length)} drawings become ${label}`
    : `${fmt(latest.length)} of ${fmt(rows.length)} drawings become ${label}; the others have newer submissions`;
}

export function ReplyForm({ rows = [], title = 'Record consultant reply', onSave, onCancel, onSaved, testId = 'reply-form', fromEmail = true, initial }) {
  const [reply, setReply] = useState(initial?.reply || '');
  const [date, setDate] = useState(initial?.date || todayIso());
  const [comments, setComments] = useState(initial?.comments || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [pasteOpen, setPasteOpen] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const applyAi = (result) => {
    if (result.reply) setReply(result.reply);
    if (result.reply_date) setDate(result.reply_date);
    if (result.comments) setComments(result.comments);
    setAiResult(result);
    setPasteOpen(false);
  };
  const ai = aiResult ? aiSummary(aiResult, rows) : null;
  const max = todayIso();
  // The reply can't come back before the newest submission of the set was sent.
  const min = useMemo(() => rows.map((row) => (row.sent?.ok && !row.sent?.needsCheck ? row.sent.iso : '')).filter(Boolean).sort().pop() || '', [rows]);
  const first = rows[0];
  const sub = rows.length === 1 && first
    ? `${realDrawingId(first.drawing.drawingId) || drawingTitle(first.drawing)} · sent ${first.sent?.label || '—'}`
    : `${fmt(rows.length)} submissions`;

  const submit = async (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (!reply || !rows.length) return;
    setSaving(true);
    setError('');
    try {
      await onSave({ reply, date, comments: comments.trim() });
      onSaved?.();
    } catch (err) {
      setError(getErrorMessage(err, 'Could not save the reply'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-2.5" data-testid={testId}>
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <p className="text-[13px] font-bold text-ink">{title}</p>
          <p className="mt-0.5 truncate font-mono text-[11px] text-ink-3" title={sub}>{sub}</p>
        </div>
        {fromEmail && !pasteOpen ? (
          <Button
            type="button"
            variant="outline"
            size="xs"
            className="shrink-0 border-[color:var(--brand-soft-border)] bg-[color:var(--navy-50)] text-brand-ink"
            onClick={() => setPasteOpen(true)}
            title="Paste the consultant’s reply email; the AI reads the code and the comments"
            data-testid="reply-from-email"
          >
            <Sparkles aria-hidden="true" /> From email
          </Button>
        ) : null}
      </div>
      {pasteOpen ? <PasteEmail rows={rows} onParsed={applyAi} onClose={() => setPasteOpen(false)} /> : null}
      {ai ? (
        <div className="rounded-lg bg-[color:var(--navy-50)] px-2.5 py-1.5 text-[11.5px] text-ink-2" data-testid="reply-ai-result">
          <p><Sparkles className="mr-1 inline h-3 w-3 text-brand-ink" aria-hidden="true" />Filled from the email: {ai.line}. Check it, then save.</p>
          {ai.warnings.map((warning) => (
            <p key={warning} className="mt-0.5 font-semibold text-status-amber-ink"><TriangleAlert className="mr-1 inline h-3 w-3" aria-hidden="true" />{warning}</p>
          ))}
        </div>
      ) : null}
      <div className="grid grid-cols-2 gap-1" role="radiogroup" aria-label="Consultant reply code">
        {REPLY_CODES.map((item) => {
          const active = reply === item.value;
          return (
            <button
              key={item.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => setReply(item.value)}
              className={cn(
                // The picked code is marked by its border and fill, not a bolder label: bold would cut
                // "Approved w/ comments" in the half-width cell.
                'flex h-9 items-center gap-2 rounded-lg border px-1.5 text-left text-[12.5px] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                active ? 'border-[color:var(--brand-ink)] bg-brand-soft text-ink shadow-[inset_0_0_0_1px_var(--brand-ink)]' : 'border-line text-ink hover:bg-surface-hover'
              )}
              data-testid={`reply-code-${item.code}`}
            >
              <ReplyCodeBadge code={item.code} tone={item.tone} />
              <span className="min-w-0 flex-1 truncate">{SHORT_CODE_LABELS[item.code] || item.label}</span>
            </button>
          );
        })}
      </div>
      <div className="h-px bg-line-2" />
      <label className="block text-[12px] font-semibold text-ink-2">
        Reply date
        <span className="mt-1 block">
          <input
            type="date"
            value={date}
            min={min || undefined}
            max={max}
            required
            onChange={(event) => setDate(event.target.value)}
            className="h-9 w-full rounded-control border border-line bg-surface px-2.5 text-[13px] text-ink outline-none focus:border-[color:var(--brand-ink)] focus:ring-2 focus:ring-[color:var(--brand-soft-border)]"
            data-testid="reply-date"
          />
        </span>
      </label>
      <label className="block text-[12px] font-semibold text-ink-2">
        <span className="sr-only">Consultant comments</span>
        <textarea
          value={comments}
          onChange={(event) => setComments(event.target.value)}
          placeholder="Paste the consultant comments…"
          maxLength={4000}
          className="h-14 w-full resize-none rounded-control border border-line bg-surface px-2.5 py-2 text-[13px] font-normal text-ink outline-none placeholder:text-ink-4 focus:border-[color:var(--brand-ink)] focus:ring-2 focus:ring-[color:var(--brand-soft-border)]"
          data-testid="reply-comments"
        />
      </label>
      {error ? <p className="text-[12px] font-semibold text-status-red-ink" role="alert">{error}</p> : null}
      <div className="flex items-center justify-between gap-2">
        <p className="min-w-0 text-[11.5px] text-ink-3" data-testid="reply-effect">{replyEffect(rows, reply)}</p>
        <div className="flex shrink-0 items-center gap-1.5">
          {onCancel ? <Button type="button" variant="ghost" size="sm" onClick={onCancel} disabled={saving}>Cancel</Button> : null}
          <Button type="submit" size="sm" disabled={!reply || !rows.length || saving} data-testid="save-reply">
            {saving ? <Loader2 className="animate-spin" aria-hidden="true" /> : null}
            {saving ? 'Saving…' : 'Save reply'}
          </Button>
        </div>
      </div>
      {min ? <p className="sr-only">Sent {formatDate(min)}; the reply date can't be earlier.</p> : null}
    </form>
  );
}

/** "Record reply" button + popover with the reply form. */
export function RecordReplyPopover({ rows, onSave, label = 'Record reply', align = 'end', size = 'xs', variant = 'outline', className, testId, title }) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button type="button" variant={variant} size={size} className={className} data-testid={testId} disabled={!rows.length}>
          <Reply aria-hidden="true" /> {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align={align}
        sideOffset={4}
        className="w-[400px] max-w-[calc(100vw-24px)] rounded-xl border-line bg-surface p-3.5 text-ink shadow-overlay"
        onClick={(event) => event.stopPropagation()}
        onKeyDown={(event) => event.stopPropagation()}
      >
        {open ? <ReplyForm rows={rows} title={title} onSave={onSave} onSaved={() => setOpen(false)} onCancel={() => setOpen(false)} /> : null}
      </PopoverContent>
    </Popover>
  );
}
